import { bindServices } from '@/services/api';
import { hydrateCollaboratorUsers } from '@/utils/collaborators';
import {
  buildReportIdentityPayload,
  buildUpdatePermissionParameters,
  flattenAccessControl,
  flattenAllowedSubjects,
  unwrapAllowedList,
  unwrapReportEntity,
} from '@/pages/configurations/reports/reportContract';
import { collectDatasetResourceRefs, unwrapCatalogItem, unwrapCatalogList } from './utils';

const api = bindServices('reportBuilder');

const initialState = {
  currentReport: null,
  draft: {},
  configurations: null,
  generatedToken: null,
  versions: [],
  collaborators: [],
  reportSaving: false,
  catalog: [],
  catalogLoading: false,
  resolvedByKey: {},
  resourceNames: {},
};

function* getTokenContext(select) {
  const tk = yield select((s) => s.reportBuilder.generatedToken || {});
  const current = yield select((s) => s.reportBuilder.currentReport || {});
  return {
    reportId: tk.reportId || current.id,
    versionId: tk.versionId,
    token: tk.token,
  };
}

const Model = {
  namespace: 'reportBuilder',
  state: initialState,
  effects: {
    *initBuilder({ payload }, { call, put }) {
      const { reportId } = payload || {};
      if (!reportId) return;
      const load = function* (expand) {
        const item = yield call(api.getById, {
          route: 'report',
          id: reportId,
          ...(expand ? { expand } : {}),
        });
        const entity = unwrapReportEntity(item);
        if (!entity?.id) throw new Error('report-not-found');
        return {
          ...entity,
          ...flattenAccessControl(entity?.accessControl),
          owner: entity?.ownerUser || entity?.owner,
        };
      };
      try {
        const report = yield* load({
          reportGroup: { select: 'id,name' },
          ownerUser: { select: 'id,fullName' },
        });
        yield put({ type: 'saveReport', payload: report });
      } catch (e) {
        try {
          const report = yield* load();
          yield put({ type: 'saveReport', payload: report });
        } catch (_retry) {
          yield put({ type: 'saveReport', payload: null });
        }
      }
    },

    *saveReportChanges({ payload }, { call, put, select }) {
      try {
        yield put({ type: 'setSaving', payload: true });
        const current = yield select((s) => s.reportBuilder.currentReport) || {};
        const merged = {
          ...current,
          ...payload,
          id: current.id,
          provider: current.provider,
        };
        const data = buildReportIdentityPayload(merged);
        const res = yield call(api.update, { route: 'report', data });
        const next = res?.data || res || { ...current, ...payload };
        yield put({
          type: 'saveReport',
          payload: {
            ...current,
            ...next,
            ...flattenAccessControl(next.accessControl || payload),
          },
        });
        yield put({ type: 'clearDraft' });
        return true;
      } finally {
        yield put({ type: 'setSaving', payload: false });
      }
    },

    *fetchConsumeAccess({ payload }, { call, put, select }) {
      const reportId =
        payload?.reportId || (yield select((s) => s.reportBuilder.currentReport?.id));
      if (!reportId) return;
      try {
        const users = yield call(api.getAll, {
          route: `report/allowedUsers/${reportId}`,
          params: { select: 'id,fullName,email', orderBy: 'fullName' },
        });
        const positions = yield call(api.getAll, {
          route: `report/allowedPositions/${reportId}`,
          params: { select: 'id,name', orderBy: 'name' },
        });
        const areas = yield call(api.getAll, {
          route: `report/allowedAreas/${reportId}`,
          params: { select: 'id,name', orderBy: 'name' },
        });
        const userGroups = yield call(api.getAll, {
          route: `report/allowedUserGroups/${reportId}`,
          params: { select: 'id,name', orderBy: 'name' },
        });
        const consumeAccess = flattenAllowedSubjects({
          users: unwrapAllowedList(users),
          positions: unwrapAllowedList(positions),
          areas: unwrapAllowedList(areas),
          userGroups: unwrapAllowedList(userGroups),
        });
        const current = yield select((s) => s.reportBuilder.currentReport) || {};
        yield put({
          type: 'saveReport',
          payload: {
            ...current,
            ...consumeAccess,
            accessControl: {
              ...(current.accessControl || {}),
              ...consumeAccess,
            },
          },
        });
      } catch (_) {
        /* keep persisted accessControl JSON */
      }
    },

    *saveConsumeAccess({ payload }, { call, put, select }) {
      const current = yield select((s) => s.reportBuilder.currentReport) || {};
      const reportId = current.id;
      if (!reportId) return false;
      try {
        yield put({ type: 'setSaving', payload: true });
        yield call(api.apiRoute, {
          route: `report/updatePermissions/${reportId}`,
          method: 'post',
          data: buildUpdatePermissionParameters(payload),
        });
        const consumeAccess = flattenAllowedSubjects(payload);
        yield put({
          type: 'saveReport',
          payload: {
            ...current,
            ...consumeAccess,
            accessControl: {
              ...(current.accessControl || {}),
              ...consumeAccess,
            },
          },
        });
        return true;
      } finally {
        yield put({ type: 'setSaving', payload: false });
      }
    },

    *fetchCollaborators({ payload }, { call, put, select }) {
      const reportId =
        payload?.reportId || (yield select((s) => s.reportBuilder.currentReport?.id));
      if (!reportId) return;
      try {
        const res = yield call(api.getAll, {
          route: `report/config/collaborators/${reportId}`,
          ...(payload?.readPriority && { readPriority: true }),
        });
        const raw = res?.items || res?.data?.items || res || [];
        const hydrated = yield call(hydrateCollaboratorUsers, Array.isArray(raw) ? raw : []);
        yield put({ type: 'saveCollaborators', payload: hydrated });
      } catch (e) {
        yield put({ type: 'saveCollaborators', payload: [] });
      }
    },

    *addCollaborator({ payload }, { call, put, select }) {
      const reportId =
        payload?.reportId || (yield select((s) => s.reportBuilder.currentReport?.id));
      if (!reportId || !payload?.collaboratorId) return;
      yield call(api.create, {
        route: `report/config/collaborators/${reportId}`,
        data: { collaboratorId: payload.collaboratorId },
      });
      yield put({ type: 'fetchCollaborators', payload: { reportId, readPriority: true } });
    },

    *removeCollaborator({ payload }, { call, put, select }) {
      const reportId =
        payload?.reportId || (yield select((s) => s.reportBuilder.currentReport?.id));
      if (!reportId || !payload?.id) return;
      yield call(api.apiRoute, {
        route: `report/config/collaborators/${reportId}/${payload.id}`,
        method: 'delete',
      });
      yield put({ type: 'fetchCollaborators', payload: { reportId, readPriority: true } });
    },

    *fetchCatalog({ payload }, { call, put, select }) {
      const { catalog, catalogLoading } = yield select((s) => s.reportBuilder);
      if (catalogLoading) return catalog;
      if (catalog?.length && !payload?.force) return catalog;
      yield put({ type: 'setCatalogLoading', payload: true });
      try {
        const res = yield call(api.getAll, { route: 'report/config/catalog' });
        const list = unwrapCatalogList(res);
        yield put({ type: 'saveCatalog', payload: list });
        yield put({ type: 'seedResolvedFromCatalog', payload: list });
        return list;
      } catch (_e) {
        yield put({ type: 'saveCatalog', payload: [] });
        return [];
      } finally {
        yield put({ type: 'setCatalogLoading', payload: false });
      }
    },

    *resolveCatalogKey({ payload }, { call, put, select }) {
      const key = typeof payload === 'string' ? payload : payload?.key;
      if (!key) return null;
      const cache = yield select((s) => s.reportBuilder.resolvedByKey) || {};
      if (cache[key]?.fields && !payload?.force) return cache[key];
      const catalog = yield select((s) => s.reportBuilder.catalog) || [];
      const listed = catalog.find((item) => item.key === key && Array.isArray(item.fields));
      if (listed && !payload?.force) {
        yield put({ type: 'saveResolvedItem', payload: { key, item: listed } });
        return listed;
      }
      try {
        const res = yield call(api.getAll, {
          route: 'report/config/catalog/resolve',
          passthroughParams: { key },
          suppressErrorNotification: true,
        });
        const item = unwrapCatalogItem(res);
        if (item) {
          yield put({ type: 'saveResolvedItem', payload: { key, item } });
        }
        return item;
      } catch (_e) {
        return null;
      }
    },

    *resolveDatasetResources({ payload }, { call, put, select }) {
      const items = payload?.items || [];
      if (!items.length) return;
      const names = yield select((s) => s.reportBuilder.resourceNames) || {};
      const refs = collectDatasetResourceRefs(items);
      const missing = (ids = []) => ids.filter((id) => id && !names[id]);
      const next = {};

      const remember = (rows = []) => {
        rows.forEach((row) => {
          const label = row?.displayName || row?.name;
          if (!label) return;
          [row?.formId, row?.id, row?.value].filter(Boolean).forEach((id) => {
            next[id] = label;
          });
        });
      };

      const entityIds = missing(refs.entityIds);
      if (entityIds.length) {
        try {
          const res = yield call(api.getAll, {
            route: 'entity/allowed',
            select: 'id,displayName',
            filter: entityIds.map((id) => `id eq ${id}`).join(' or '),
            limit: entityIds.length,
            suppressErrorNotification: true,
          });
          remember(unwrapCatalogList(res));
        } catch (_e) {
          /* keep card without entity name */
        }
      }

      const processIds = missing(refs.processIds);
      if (processIds.length) {
        try {
          const res = yield call(api.getAll, {
            route: 'process/allowed',
            select: 'id,name',
            filter: processIds.map((id) => `id eq ${id}`).join(' or '),
            limit: processIds.length,
            suppressErrorNotification: true,
          });
          remember(unwrapCatalogList(res));
        } catch (_e) {
          /* keep card without process name */
        }
      }

      for (const [processId, formIds] of Object.entries(refs.formsByProcess || {})) {
        const pendingForms = missing(formIds);
        if (!pendingForms.length) continue;
        try {
          const res = yield call(api.getAll, {
            route: `process/config/${processId}/published/forms`,
            select: 'id,name,formId,internalName',
            filter: 'isActive eq true',
            limit: 200,
            suppressErrorNotification: true,
          });
          remember(unwrapCatalogList(res));
        } catch (_e) {
          /* keep card without form name */
        }
      }

      if (Object.keys(next).length) {
        yield put({ type: 'saveResourceNames', payload: next });
      }
    },

    *appendElement({ payload }, { call, select }) {
      const { elementType, data } = payload;
      const { reportId, versionId, token } = yield* getTokenContext(select);
      if (!reportId || !versionId || !token) {
        throw new Error('missing-session');
      }
      return yield call(api.apiRoute, {
        route: `report/config/append/${reportId}/${versionId}`,
        params: { token },
        method: 'post',
        data: { elementType, data },
      });
    },

    *modifyElement({ payload }, { call, select }) {
      const { elementType, itemId, itemKey, data } = payload;
      const { reportId, versionId, token } = yield* getTokenContext(select);
      if (!reportId || !versionId || !token) {
        throw new Error('missing-session');
      }
      return yield call(api.apiRoute, {
        route: `report/config/modify/${reportId}/${versionId}`,
        params: { token },
        method: 'post',
        data: { elementType, itemId, itemKey, data },
      });
    },

    *removeElement({ payload }, { call, select }) {
      const { elementType, itemId, itemKey } = payload;
      const { reportId, versionId, token } = yield* getTokenContext(select);
      if (!reportId || !versionId || !token) {
        throw new Error('missing-session');
      }
      return yield call(api.apiRoute, {
        route: `report/config/remove/${reportId}/${versionId}`,
        params: { token },
        method: 'post',
        data: { elementType, itemId, itemKey },
      });
    },
  },
  reducers: {
    saveReport(state, { payload }) {
      return { ...state, currentReport: payload };
    },
    saveToken(state, { payload }) {
      return { ...state, generatedToken: payload };
    },
    saveVersions(state, { payload }) {
      return { ...state, versions: payload || [] };
    },
    saveConfigurations(state, { payload }) {
      return { ...state, configurations: payload };
    },
    updateDraft(state, { payload }) {
      return { ...state, draft: { ...(state.draft || {}), ...(payload || {}) } };
    },
    clearDraft(state) {
      return { ...state, draft: {} };
    },
    saveCollaborators(state, { payload }) {
      return { ...state, collaborators: payload || [] };
    },
    setSaving(state, { payload }) {
      return { ...state, reportSaving: !!payload };
    },
    setCatalogLoading(state, { payload }) {
      return { ...state, catalogLoading: !!payload };
    },
    saveCatalog(state, { payload }) {
      return { ...state, catalog: payload || [] };
    },
    seedResolvedFromCatalog(state, { payload }) {
      const next = { ...(state.resolvedByKey || {}) };
      (payload || []).forEach((item) => {
        if (item?.key && Array.isArray(item.fields)) {
          next[item.key] = item;
        }
      });
      return { ...state, resolvedByKey: next };
    },
    saveResolvedItem(state, { payload }) {
      const { key, item } = payload || {};
      if (!key || !item) return state;
      return {
        ...state,
        resolvedByKey: { ...(state.resolvedByKey || {}), [key]: item },
      };
    },
    saveResourceNames(state, { payload }) {
      if (!payload || typeof payload !== 'object') return state;
      return {
        ...state,
        resourceNames: { ...(state.resourceNames || {}), ...payload },
      };
    },
    resetBuilder() {
      return { ...initialState };
    },
  },
};

export default Model;
